/**
 * Einen festen Satz in Vesperas Stimme sprechen — über DENSELBEN Weg wie ihre Antworten. (17.10.2026, Jarvis-Hörprobe)
 *
 * Die Hörprobe der Sofort-Sätze (lib/reflex-hoerprobe-karte.ts) soll so klingen, wie Vespera später wirklich klänge:
 *   - Vertonung über app.ts `fetchTtsChunk` (Datei-, PCM- oder Strom-Weg nach denselben Schaltern, Stimme und Tempo aus der
 *     Konto-Einstellung, derselbe Zwischenspeicher). app.ts meldet den Weg hier an (`setzeVesperaTonweg`) — die Karte kennt
 *     app.ts nicht, und es gibt keine zweite Kopie der Stimmwahl.
 *   - Abspielen über eine eigene Vorlese-Schlange (lib/tts-queue.ts): sie sendet `voice-v8:tts-audio-start/-end/-abbruch` —
 *     die Tagesaufnahme pausiert (ton-rekorder-schonung.ts), die Mikro-Spur erfährt es (mikro-spur.ts).
 *   - Die Bühne des Ton-Leitstands (ton-leitstand.ts) wird betreten: eine laufende Spur (Meditation) wird freundlich
 *     angehalten, statt dass die Probe darüber klingt; wer die Bühne danach nimmt, bricht die Probe ab.
 *
 *   - Läuft eine lange Spur (lib/lange-spur.ts: Meditation, Spiegel, Biografie, Musik), startet die Probe NICHT (`belegt`,
 *     vor dem Betreten der Bühne) — sie hielte sonst die Spur an. Ebenso nicht, solange Vespera spricht.
 *   - ⚠️ Der Beginn eines Probesatzes meldet sich wie jede Stimme beim Konto (seiten-abgleich.ts `stimmeBeginnt`) und kann
 *     eine Stimme auf einem ANDEREN Gerät beenden („wer zuletzt beginnt, spricht“). Das geschieht nur durch Martins Tipp
 *     auf „Anhören“.
 */
import { betritt } from "./ton-leitstand.js";
import { loadSettings } from "./settings.js";
import { TtsQueue } from "./tts-queue.js";

type HoleTon = (text: string, signal: AbortSignal) => Promise<string>;
interface Tonweg {
  holeTon: HoleTon;
  /**
   * Was den Start verbietet: eine lange Spur läuft (Meditation, Musik — lange-spur.ts) oder eine Antwort spricht/wartet.
   * Geprüft VOR dem Betreten der Bühne — sonst hielte die Probe die Spur an.
   */
  belegt?: () => "lange-spur" | "antwort" | null;
  /** Die Schlange beim globalen Stopp (Pille, Ton aus) anmelden; gibt das Abmelden zurück. */
  melde?: (q: TtsQueue) => () => void;
}

let tonweg: Tonweg | null = null;

/** app.ts meldet hier Vesperas Vertonungs-Weg an (`fetchTtsChunk`). */
export function setzeVesperaTonweg(w: Tonweg | null): void {
  tonweg = w;
}

/** Ist der Weg angemeldet (läuft die App)? */
export function vesperaTonwegDa(): boolean {
  return tonweg !== null;
}

/**
 * Spricht `text` einmal und kehrt nach dem Ende zurück. Wirft, wenn kein Weg angemeldet ist oder nichts gespielt wurde.
 * Abbruch über `signal` (oder einen anderen Ton auf der Bühne) beendet ohne Fehler.
 */
export async function sprichVesperaSatz(text: string, signal: AbortSignal, titel = "Hörprobe"): Promise<void> {
  const w = tonweg;
  if (!w) throw new Error("kein-tonweg");
  if (signal.aborted) return;
  const belegt = w.belegt?.() ?? null;
  if (belegt) throw new Error(belegt === "lange-spur" ? "lange-spur" : "belegt");
  let gespielt = false;
  const q = new TtsQueue({
    fetchTts: (t, s) => w.holeTon(t, s),
    // Eigener Sprecher: das Ende eines Probesatzes ist keine Antwort Vesperas — kein Mikro, kein Halt-Ende (app.ts).
    sprecher: "probe",
    direktBeta: () => loadSettings().ttsDirektBeta === true,
    onAudioStart: () => {
      gespielt = true;
      platz.melde(true);
    },
  });
  const platz = betritt({ art: "vorlesen", titel, anhalten: () => q.cancel("leitstand"), beenden: () => q.cancel("leitstand") });
  const abmelden = w.melde?.(q) ?? null;
  const ab = (): void => q.cancel("hoerprobe-abbruch");
  signal.addEventListener("abort", ab, { once: true });
  try {
    q.push(text);
    q.flush();
    await q.idle();
  } finally {
    signal.removeEventListener("abort", ab);
    abmelden?.();
    if (platz.gilt) platz.verlasse();
  }
  if (signal.aborted) return;
  // Von fremder Hand abgebrochen (Stopp-Pille, neue Antwort, anderer Ton auf der Bühne): der Satz gilt als nicht gehört.
  const abbruch = q.sprechBilanz().abbruch;
  if (abbruch) throw new Error("unterbrochen");
  if (!gespielt) throw new Error("nicht-gespielt");
}
